import { useEffect, useContext, useState } from 'react';
import type { CustomNode, CustomEdge, CustomNodeData } from '@src/components/custom-nodes/types';
import { buildLinkedListHashTable } from '@src/scenario-editor/utils/serializer';
import { validatorInstance, initInstanceWithDefinitions, type NodeDefinitionItem } from '@src/scenario-editor/validator/Validator';
import { getDndNodeItem } from '@src/atoms';
import { useAtom } from 'jotai';
import { FetchContext } from '@src/scenario-editor/providers/fetch.context';
import type { CustomGenericNodeData } from '@src/components/custom-nodes/types';
import {
  historyPushAtom,
  nodeDefinitionsAtom,
  getHistoryNavigationFlag,
  type ValidationResultItem
} from '@src/atoms';
import { ALLOWED_GOTO_TYPES } from '@src/consts';
import { notifyConsumer, type CallbackFn } from '@src/scenario-editor/hooks/notify-consumer';

const PAYLOAD_KEY = 'scenario-editor';

const debounce = (fn: (nodes: CustomNode[], edges: CustomEdge[], callback?: CallbackFn, isInitialized?: boolean) => void, ms: number) => {
  let timeout: NodeJS.Timeout;
  return (nodes: CustomNode[], edges: CustomEdge[], callback?: CallbackFn, isInitialized?: boolean) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(nodes, edges, callback, isInitialized), ms);
  };
}

const getValidationResults = (nodes: CustomNode[]) => {
  return nodes.map((node) => {
    const nodeData = node?.data as CustomNodeData;
    let validationResult = null;
    if (validatorInstance.isRuleImplemented(`${nodeData?.nodeType ?? ''}:${node?.id}`)) {
      validationResult = validatorInstance.validate(`${nodeData?.nodeType ?? ''}:${node?.id}`, nodeData)
    } else if (validatorInstance.isRuleImplemented(nodeData?.nodeType ?? '')) {
      validationResult = validatorInstance.validate(nodeData?.nodeType ?? '', nodeData);
    }

    return {
      ...validationResult,
      id: node.id,
    } as ValidationResultItem;
  }).filter((validationItem) => validationItem && validationItem?.success === false);
};

const saveToLocalStorage = debounce((
    nodes: CustomNode[],
    edges: CustomEdge[],
    callback?: CallbackFn,
    isInitialized?: boolean
  ) => {
  const validationResults = getValidationResults(nodes);
  console.log('validationResults', validationResults);
  const payload = buildLinkedListHashTable(nodes, edges)
  localStorage.setItem(PAYLOAD_KEY, JSON.stringify(payload));

  if (!isInitialized) {
    return;
  }

  notifyConsumer(payload, validationResults as never, callback);
}, 500);

export const useSave = (nodes: CustomNode[], edges: CustomEdge[], callback?: CallbackFn) => {
  const [, pushHistoryRecord] = useAtom(historyPushAtom);
  const [nodeDefinitions] = useAtom(nodeDefinitionsAtom);
  const fetchContext = useContext(FetchContext)!;
  const [isInitialized, setIsInitialized] = useState(false);

  // ==================================================
  // INFO: this part should be moved to a separate hook

  useEffect(() => {
    initInstanceWithDefinitions(nodeDefinitions as Record<string, NodeDefinitionItem>);

    Promise.all([
      fetchContext.fetchFitFacts()
        .then((data) => {
          validatorInstance.setFetchContext('fit:facts', data as never);
          validatorInstance.updateDynamicParamsForSchema('fit:facts', {
            ids: data.data.ids.map(item => item.id),
            categories: data.data.categories.map(item => item.id),
            tags: data.data.tags.map(item => item.id),
          })
        })
        .catch((error) => {
          console.error('Error fetching fit facts', error);
        }),

      fetchContext.fetchArticles()
        .then((data) => {
          validatorInstance.setFetchContext('message:suggestions', data as never);
          validatorInstance.updateDynamicParamsForSchema('message:suggestions', {
            ids: data.data.ids.map(item => item.id),
            categories: data.data.categories.map(item => item.id),
            tags: data.data.tags.map(item => item.id)
          })
        })
        .catch((error) => {
          console.log('Error fetching articles', error);
        }),

      fetchContext.fetchAllScenario()
        .then((data) => {
          validatorInstance.setFetchContext('scenario:picker', data as never);
          validatorInstance.updateDynamicParamsForSchema('scenario:picker', {
            scenarioValue: data.data.map(item => item.id)
          })
        })
        .catch((error) => {
          console.error('Error fetching scenarios', error);
        })
    ]).then(() => {
      setIsInitialized(true);
    }).catch((error) => {
      console.error('Error during initialization:', error);
      setIsInitialized(true); // Set initialized even if some fetches fail
    });

    const tasksOptions =
      (nodeDefinitions?.['task:*']?.nodeInfoContent ?? [])
        .find(item => item.fieldName === 'nodeType')?.options ?? [];

    validatorInstance.updateDynamicParamsForSchema('task:*', {
      tasks: tasksOptions.map(item => item.value)
    })

    const gotoPickerOptions = nodes.filter((node) => ALLOWED_GOTO_TYPES.includes((node.data as CustomGenericNodeData)?.nodeType ?? ''));
    validatorInstance.updateDynamicParamsForSchema('message:goto', {
      goto: gotoPickerOptions.map(item => item.id)
    })
  }, [nodeDefinitions, fetchContext, nodes.length]);
  // ==================================================

  // Re-fire saveToLocalStorage once init completes so any node changes
  // the user made while validators were still warming up are propagated
  // to the consumer callback. Without this, a saveToLocalStorage call
  // scheduled before init returns early and the next nodes-change is
  // required for the callback to ever fire - meaning the user's first
  // edit after opening a scenario can be silently dropped.
  useEffect(() => {
    if (!isInitialized) return;
    const arePlaceholdersRendered = nodes.some(node => node.type === 'PlaceholderNode');
    const dndNodeItem = getDndNodeItem();
    if (arePlaceholdersRendered || dndNodeItem) return;
    saveToLocalStorage(nodes, edges, callback, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized]);

  useEffect(() => {
    const historyNavigationFlag = getHistoryNavigationFlag();
    const arePlaceholdersRendered = nodes.some(node => node.type === 'PlaceholderNode');
    const dndNodeItem = getDndNodeItem();

    // INFO: do not save to local storage if placeholders are rendered or dnd node is in progress
    // move this to a separate logic at some point
    if (!arePlaceholdersRendered && !dndNodeItem) {
      saveToLocalStorage(nodes, edges, callback, isInitialized);

      const payload = buildLinkedListHashTable(nodes, edges);
      if (Object.keys(payload).length && !historyNavigationFlag) {
        console.log('pushing payload to history', payload);
        pushHistoryRecord(payload);
      }
    }
  }, [JSON.stringify(nodes)]);

  return localStorage.getItem(PAYLOAD_KEY);
};
